import React, { useMemo, useState } from "react";
import { Dialog, Drawer } from "@mui/material";
import { I } from "./icons";
import "./globalAdmin.css";

/**
 * Shared pieces for the Global Admin console: one status treatment, one environment badge, one confirmation pattern.
 * Markup and class names follow the Claude Design handoff; the styles are in globalAdmin.css, scoped under .ga-root.
 * Colour is never the only signal: every status chip carries an icon and a word.
 */

/** Every status the console shows, mapped to a tone and an icon. Labels default to the status word. */
const STATUS = {
  OK: ["ok", I.check], Active: ["ok", I.check], Verified: ["ok", I.check], Published: ["ok", I.check], Sent: ["ok", I.check], Success: ["ok", I.check], Live: ["ok", I.check],
  Warn: ["warn", I.alert], Degraded: ["warn", I.alert],
  Down: ["bad", I.stop], Failed: ["bad", I.stop], Failure: ["bad", I.stop], Refused: ["bad", I.stop],
  Draft: ["info", I.pencil], "New version in progress": ["info", I.pencil], "In progress": ["info", I.clock],
  Retired: ["muted", I.archive], Inactive: ["muted", I.minus],
  Unknown: ["unk", I.help], Unverified: ["unk", I.help], "Not published": ["unk", I.help],
};

export function StatusChip({ status, label, solid }) {
  const [tone, Icon] = STATUS[status] || ["muted", I.minus];
  return <span className={`chip ${tone}${solid ? " solid" : ""}`}><Icon s={12} />{label || status}</span>;
}

const ENV_CLASS = { local: "local", dev: "dev", production: "prod", prod: "prod" };
const ENV_TEXT = { local: "Local", dev: "Dev", production: "Production", prod: "Production" };

/** Environment badge. Production is near-black with a lock so it never reads as a development environment. */
export function EnvBadge({ env, label }) {
  const cls = ENV_CLASS[env] || "dev";
  return <span className={`envb ${cls}`}>{cls === "prod" && <I.lock s={11} />}{label || ENV_TEXT[env] || env}</span>;
}

/** Programme / category label: a coloured dot and an uppercase word. */
const CAT_COLOR = {
  Firefighter: "var(--color-cat-firefighter)", EMS: "var(--color-cat-ems)", General: "var(--color-cat-other)", AHA: "var(--color-cat-aha)",
  Rescue: "var(--color-cat-rescue)", Hazmat: "#b45309", "Fire Officer": "var(--color-cat-security)", Police: "var(--color-cat-police)",
};
export function Cat({ c }) {
  const color = CAT_COLOR[c] || "var(--color-cat-other)";
  return <span className="cat" style={{ color }}><i style={{ background: color }} />{c}</span>;
}

/** Title, one line of plain description, and the view's actions on the right. */
export function PageHeader({ title, desc, crumbs, actions }) {
  return (
    <div className="ph">
      <div style={{ minWidth: 0, flex: "1 1 360px" }}>
        {crumbs}
        <h1>{title}</h1>
        {desc && (typeof desc === "string" ? <p>{desc}</p> : <div className="desc">{desc}</div>)}
      </div>
      {actions && <div className="act">{actions}</div>}
    </div>
  );
}

/** Breadcrumb line above a page title. `trail` is a list of { label, onClick? }; the last item is the current page. */
export function Crumbs({ trail }) {
  return (
    <div className="crumbs">
      {trail.map((item, i) => (
        <React.Fragment key={item.label}>
          {i > 0 && <span aria-hidden="true">/</span>}
          {item.onClick ? <button type="button" onClick={item.onClick}>{item.label}</button> : <span>{item.label}</span>}
        </React.Fragment>
      ))}
    </div>
  );
}

const NOTICE_ICON = { info: I.info, warn: I.alert, bad: I.stop, ok: I.check, prod: I.lock, neutral: I.info };
export function Notice({ kind = "info", children }) {
  const Icon = NOTICE_ICON[kind] || I.info;
  return <div className={`notice ${kind}`} role={kind === "bad" ? "alert" : undefined}><Icon s={15} /><div>{children}</div></div>;
}

/** Stat tiles. When `onPick` is given each tile with a `key` is a filter; the active one shows as "Filtering". */
export function StatTiles({ items, active, onPick }) {
  return (
    <div className="tiles">
      {items.map((item) => {
        const on = active === item.key;
        const body = (
          <>
            <span className="l">{item.icon}{item.label}</span>
            <span className="n" style={{ color: item.tone }}>{item.value}</span>
            {item.foot && <span style={{ fontSize: 12, color: "var(--ga-sub)" }}>{item.foot}</span>}
          </>
        );
        return onPick && item.key ? (
          <button type="button" key={item.key} className={`tile${on ? " on" : ""}`} aria-pressed={on} onClick={() => onPick(on ? null : item.key)}>
            {body}<span className="f">{on ? "Filtering" : "Filter"}</span>
          </button>
        ) : (
          <div key={item.key || (typeof item.label === "string" ? item.label : item.value)} className="tile">{body}</div>
        );
      })}
    </div>
  );
}

/** Loading, empty, error and permission-denied states. Each says what happened and what to do next. */
export function StatePanel({ kind, what = "items", why, onRetry, error, action }) {
  if (kind === "loading") {
    return (
      <div style={{ padding: 18, display: "grid", gap: 14 }} aria-busy="true" role="status">
        {[70, 90, 55, 80, 65].map((w, i) => (
          <div key={i} style={{ display: "flex", gap: 14 }}><div className="ga-sk" style={{ width: 120 }} /><div className="ga-sk" style={{ width: `${w}%` }} /></div>
        ))}
        <span className="sub" style={{ fontSize: 12.5 }}>Loading {what}…</span>
      </div>
    );
  }
  if (kind === "error") {
    return (
      <div className="state bad" role="alert">
        <span className="ic"><I.stop s={20} /></span>
        <b>Couldn't load {what}</b>
        <p>The server returned an error. Your filters are kept; try again, or check System health if it keeps failing.</p>
        {error && <code>{error}</code>}
        {onRetry && <button type="button" className="b sec" onClick={onRetry}><I.refresh s={14} />Retry</button>}
      </div>
    );
  }
  if (kind === "denied") {
    return (
      <div className="state">
        <span className="ic"><I.lock s={20} /></span>
        <b>You don't have access to this view</b>
        <p>{why || "This view is limited to global admins. Ask a global admin to change your platform role if you need it."}</p>
      </div>
    );
  }
  return (
    <div className="state">
      <span className="ic"><I.search s={20} /></span>
      <b>No {what} yet</b>
      {why && <p>{why}</p>}
      {action}
    </div>
  );
}

/** Step indicator for multi-step flows. Done steps show a tick, the current step is filled. */
export function Steps({ steps, current, error }) {
  return (
    <div className="steps">
      {steps.map((label, i) => {
        const cls = i < current ? " done" : i === current ? (error ? " err" : " on") : "";
        return <div key={label} className={`step${cls}`} aria-current={i === current ? "step" : undefined}><span className="d">{i < current ? <I.check s={12} /> : i + 1}</span>{label}</div>;
      })}
    </div>
  );
}

/** A labelled block with a heading row and optional action, used for the cards on each view. */
export function Panel({ title, subtitle, actions, children, style, className = "" }) {
  return (
    <div className={`panel ${className}`.trim()} style={style}>
      {(title || actions) && (
        <div className="panel-h">
          <div style={{ minWidth: 0 }}>
            {title && <h3>{title}</h3>}
            {subtitle && <div className="sub" style={{ fontSize: 12.5 }}>{subtitle}</div>}
          </div>
          <span className="sp" />
          {actions}
        </div>
      )}
      {children}
    </div>
  );
}

/** Pill used as a filter. `count` is shown small after the label. */
export function FilterChip({ label, count, on, onClick, icon }) {
  return (
    <button type="button" className={`fchip${on ? " on" : ""}`} aria-pressed={on} onClick={onClick}>
      {icon}{label}{count != null && <span className="c">{count}</span>}
    </button>
  );
}

/** Dialog in the console's style. MUI supplies the focus trap and Escape handling; the look is the design's. */
export function Modal({ open = true, title, sub, children, footer, onClose, wide, labelId = "ga-modal-title" }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      aria-labelledby={labelId}
      PaperProps={{ sx: { width: wide ? "min(720px, 100%)" : "min(540px, 100%)", m: 2, borderRadius: "12px", overflow: "hidden" } }}
      slotProps={{ backdrop: { sx: { background: "rgba(21,15,35,.45)" } } }}
    >
      <div className="ga-root dlg">
        <div className="dlg-h">
          <div style={{ flex: 1 }}>
            <h3 id={labelId}>{title}</h3>
            {sub && <div className="sub" style={{ fontSize: 13, marginTop: 4 }}>{sub}</div>}
          </div>
          {onClose && <button type="button" className="ib" aria-label="Close" onClick={onClose}><I.x s={18} /></button>}
        </div>
        <div className="dlg-b">{children}</div>
        {footer && <div className="dlg-f">{footer}</div>}
      </div>
    </Dialog>
  );
}

/** Side drawer for one record (an audit event, say), so the list behind it keeps its place. */
export function SideDrawer({ open, title, sub, children, footer, onClose }) {
  return (
    <Drawer anchor="right" open={open} onClose={onClose} slotProps={{ backdrop: { sx: { background: "rgba(21,15,35,.45)" } } }} PaperProps={{ sx: { width: "min(480px, 100vw)" } }}>
      <aside className="ga-root drawer" aria-label={typeof title === "string" ? title : undefined}>
        <div className="drawer-h">
          <div style={{ flex: 1, minWidth: 0 }}>{sub}<h3>{title}</h3></div>
          <button type="button" className="ib" aria-label="Close" onClick={onClose}><I.x s={18} /></button>
        </div>
        <div className="drawer-b">{children}</div>
        {footer && <div className="drawer-f">{footer}</div>}
      </aside>
    </Drawer>
  );
}

/**
 * Confirmation for a change that affects people. Says what will happen, what will not, and in production says so.
 * When `typed` is given the admin must type that exact text before the confirm button enables.
 */
export function ConfirmDialog({ open, env, title, consequence, keeps, confirmLabel, destructive = true, typed, onConfirm, onClose, children }) {
  const [text, setText] = useState("");
  const ready = !typed || text.trim() === typed;
  const close = () => { setText(""); onClose(); };
  return (
    <Modal
      open={open}
      title={title}
      onClose={close}
      labelId="confirm-dialog-title"
      footer={(
        <>
          <span className="note">{env === "production" ? "Recorded in the audit log under your name." : "Recorded in the audit log."}</span>
          <button type="button" className="b ghost" onClick={close}>Cancel</button>
          <button type="button" className={`b ${destructive ? "danger" : "pri"}`} disabled={!ready} onClick={() => { setText(""); onConfirm(); }}>{confirmLabel}</button>
        </>
      )}
    >
      {env === "production" && <Notice kind="prod">You are in <b>Production</b>. This changes live data.</Notice>}
      <p className="cons">{consequence}</p>
      {keeps && <p className="cons sub" style={{ fontSize: 13.5 }}>{keeps}</p>}
      {children}
      {typed && (
        <label className="field">
          <span>Type <span className="code">{typed}</span> to confirm</span>
          <span className="inp"><input value={text} onChange={(e) => setText(e.target.value)} autoFocus /></span>
        </label>
      )}
    </Modal>
  );
}

/** Sort state for a table. Returns the sorted rows and a `th(key, label, className)` header factory. */
export function useSort(rows, initial) {
  const [sort, setSort] = useState(initial);
  const sorted = useMemo(() => {
    if (!sort) return rows;
    const [key, dir] = sort;
    return [...rows].sort((a, b) => {
      const av = a[key], bv = b[key];
      const cmp = typeof av === "string" && typeof bv === "string" ? av.localeCompare(bv) : (av > bv ? 1 : av < bv ? -1 : 0);
      return cmp * (dir === "asc" ? 1 : -1);
    });
  }, [rows, sort]);
  const th = (key, label, cls = "") => {
    const on = sort && sort[0] === key;
    return (
      <th key={key} className={`s ${cls}${on ? " sorted" : ""}`.trim()} aria-sort={on ? (sort[1] === "asc" ? "ascending" : "descending") : "none"}
        onClick={() => setSort(on ? [key, sort[1] === "asc" ? "desc" : "asc"] : [key, "asc"])}>
        {label}<span className="ar" aria-hidden="true">{on ? (sort[1] === "asc" ? "↑" : "↓") : "↕"}</span>
      </th>
    );
  };
  return [sorted, th];
}

/** Switch with a label and a hint. */
export function Toggle({ checked, onChange, label, hint, disabled }) {
  return (
    <label className="tog">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="sw" />
      <div><b style={{ fontWeight: 600, fontSize: 13.5 }}>{label}</b>{hint && <div className="sub" style={{ fontSize: 12.5, marginTop: 2 }}>{hint}</div>}</div>
    </label>
  );
}

/** Small checkbox used in selection lists. */
export function Check({ on, onClick, label }) {
  return (
    <span role="checkbox" aria-checked={on} aria-label={label} tabIndex={0} className={`ck${on ? " on" : ""}`} onClick={onClick}
      onKeyDown={(e) => { if (e.key === " ") { e.preventDefault(); onClick(); } }}>
      {on && <I.check s={12} />}
    </span>
  );
}
